import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { cacheSync } from '@/lib/cacheSync'
import { holdsAnyRole } from '@/lib/roles'
import { useAuthStore } from '@/store/authStore'
import { cachePatch, useOptimisticMutation } from '@/lib/optimistic-mutation'
import {
  getStudentsWithGrades,
  getAssignments,
  saveGrade,
  deleteGrade,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  headlineGrade,
} from '@/lib/gradebook-api'
import type { GradebookRoster, GradebookCellGrade } from '@/lib/gradebook-api'
import type {
  Course,
  TermCourse,
  Enrollment,
  TermSubmission,
  AssignmentCategory,
  Grade,
} from '@/types/api'
import { gradebookKeys } from '@/lib/queryKeys'

/**
 * Re-exported from `lib/queryKeys`, which owns the whole key space — see the note
 * there. A query still names its key from the module it belongs to.
 */
export { gradebookKeys }

// Base data queries
//
// Written as options rather than inline, so `usePrefetchCourseScope` below can
// start the same two reads from behind a guard without mounting the page.

function coursesQuery() {
  return {
    queryKey: gradebookKeys.courses(),
    queryFn: async () => {
      const res = await api.get<{ courses: Course[] }>('/api/courses')
      return res.data.courses
    },
  }
}

function termCoursesQuery() {
  return {
    queryKey: gradebookKeys.termCourses(),
    queryFn: async () => {
      const res = await api.get<{ termCourses: TermCourse[] }>('/api/term-courses')
      return res.data.termCourses
    },
  }
}

export function useCourses() {
  return useQuery(coursesQuery())
}

export function useTermCourses() {
  return useQuery(termCoursesQuery())
}

/**
 * Start the two reads every gradebook surface resolves its scope against —
 * which courses exist, and which sections of them run in each term.
 *
 * For a guard that holds the page back on a question of its own. Everything the
 * gradebook needs hangs off the term-course id, which is resolved from these
 * two, so waiting to mount the page before asking for them put an entire round
 * trip between arriving and the first request that matters — and then another
 * between that and the marks. Nothing here depends on what the guard is waiting
 * for, so it need not wait.
 *
 * A prefetch, not a subscription: the page's own `useCourses`/`useTermCourses`
 * dedupe against the request already in flight and read the cache when it lands.
 *
 * Only for someone who may read them. Both endpoints are teacher-or-admin
 * (`requireTeacherOrAdmin`), and the guard this runs behind also fronts the
 * student and parent grade pages — so unconditionally it would fire two requests
 * per visit that can only 403, on pages that never wanted them.
 */
export function usePrefetchCourseScope(enabled = true) {
  const queryClient = useQueryClient()
  const roles = useAuthStore((state) => state.getEffectiveRoles())
  const mayReadCourseScope = holdsAnyRole(roles, ['admin', 'teacher', 'coordinator'])

  useEffect(() => {
    if (!enabled || !mayReadCourseScope) return
    void queryClient.prefetchQuery(coursesQuery())
    void queryClient.prefetchQuery(termCoursesQuery())
  }, [queryClient, enabled, mayReadCourseScope])
}

// Term-course specific queries
export function useEnrollments(termCourseId: string | null) {
  return useQuery({
    queryKey: gradebookKeys.enrollments(termCourseId!),
    queryFn: async () => {
      const res = await api.get<{ enrollments: Enrollment[] }>(
        `/api/enrollments/term-course/${termCourseId}`
      )
      return res.data.enrollments
    },
    enabled: !!termCourseId,
  })
}

/**
 * The one roster read, shared by every hook that needs part of it.
 *
 * One request. The grid is built from the enrollments the caller is already
 * holding — they carry the roster, the names and the server's weighted course
 * grade — so all this fetches is the marks. The endpoint returns the grid *and*
 * the server's grade-calculation notice, so both are cached under a single key
 * and sliced with `select` rather than fetched twice: one request, two readers.
 */
function gradebookRosterQuery(
  termCourseId: string | null,
  enrollments: Enrollment[] | undefined
) {
  // The active enrollments decide who is in the grid, so they belong in the
  // cache key and not only in the queryFn's closure — the two have to agree.
  // Without the signature in the key, an invalidation after an enrollment change
  // could refetch against a stale closure and render the wrong roster despite a
  // persisted enrollment. Sorted for a stable key; TypeIDs are lexicographically
  // sortable (decision N-2), so a plain string sort still yields a stable,
  // creation-time-ordered key.
  const activeStudentIds = (enrollments ?? [])
    .filter((e) => e.status === 'active')
    .map((e) => e.studentId)
    .sort()

  return {
    // The base students(termCourseId) key stays a prefix, so grade-save mutations
    // that invalidate by that prefix still match this query.
    queryKey: [...gradebookKeys.students(termCourseId!), activeStudentIds],
    queryFn: () => getStudentsWithGrades(termCourseId!, enrollments!),
    enabled: !!termCourseId && !!enrollments,
  }
}

export function useGradebookStudents(
  termCourseId: string | null,
  enrollments: Enrollment[] | undefined
) {
  // The headline term grade is the server's, and it moves on every grade save.
  // It reaches the cached grid through the enrollments the fetch was built from,
  // and the roster's cache key is the *set* of active students — so a refetched
  // enrollment carrying a new weighted grade lands on a key that is already
  // fresh, and the column would keep showing the number from before the mark.
  // Re-applied here instead, on the way out of the cache, from whichever
  // enrollments the caller is holding now.
  const select = useCallback(
    (roster: GradebookRoster) => {
      const byStudent = new Map(
        (enrollments ?? []).map((enrollment) => [enrollment.studentId, enrollment.currentGrade])
      )
      return roster.students.map((student) =>
        byStudent.has(student.id)
          ? { ...student, ...headlineGrade(byStudent.get(student.id)) }
          : student
      )
    },
    [enrollments]
  )

  return useQuery({
    ...gradebookRosterQuery(termCourseId, enrollments),
    select,
  })
}

/**
 * What the server says about this course's term grade — a refusal, a warning about
 * assignments excluded from it, or both (AR-11). Null when there is nothing to say.
 *
 * Shares the roster query, so opening the gradebook still makes one read. It is
 * also invalidated by every grade-save mutation, which is right: fixing a category
 * assignment should clear the warning without a reload.
 */
export function useGradeCalculationNotice(
  termCourseId: string | null,
  enrollments: Enrollment[] | undefined
) {
  return useQuery({
    ...gradebookRosterQuery(termCourseId, enrollments),
    select: (roster) => roster.gradeCalculation,
  })
}

export function useGradebookAssignments(termCourseId: string | null) {
  return useQuery({
    queryKey: gradebookKeys.assignments(termCourseId!),
    queryFn: () => getAssignments(termCourseId!),
    enabled: !!termCourseId,
  })
}

export function useTermSubmission(termCourseId: string | null) {
  return useQuery({
    queryKey: gradebookKeys.submission(termCourseId!),
    queryFn: async () => {
      try {
        const res = await api.get<{ submission: TermSubmission | null }>(
          `/api/term-submissions/term-course/${termCourseId}`
        )
        return res.data.submission
      } catch {
        return null
      }
    },
    enabled: !!termCourseId,
  })
}

export function useCategories(termCourseId: string | null) {
  return useQuery({
    queryKey: gradebookKeys.categories(termCourseId!),
    queryFn: async () => {
      const res = await api.get<{ categories: AssignmentCategory[] }>(
        `/api/categories/term-course/${termCourseId}`
      )
      return res.data.categories
    },
    enabled: !!termCourseId,
  })
}

// Mutations
//
// Grade edits are the app's highest-frequency write, and the one place the
// old "invalidate and hope" pattern was most visible: a teacher tabbing across
// a row saw each cell show its previous score for the length of a round trip.
// The roster cache is patched in place instead, then reconciled by the awaited
// invalidation. See `lib/optimistic-mutation.ts`.

/** The roster's cached shape, patched by every grade mutation below. */
type CachedAssignments = Awaited<ReturnType<typeof getAssignments>>

/**
 * Rewrite one cell of the cached roster.
 *
 * `currentGrade` and `predictedIbGrade` are deliberately left as they are: they
 * are the server's category-weighted numbers, and guessing them here would put a
 * figure on screen that the recalculation is about to contradict. The cell the
 * teacher just typed updates instantly; the headline follows when the awaited
 * refetch lands.
 */
function patchRosterCell(
  roster: GradebookRoster,
  studentId: string,
  assignmentId: string,
  nextCell: GradebookCellGrade | null
): GradebookRoster {
  return {
    ...roster,
    students: roster.students.map((student) => {
      if (student.id !== studentId) return student
      const grades = { ...student.grades }
      if (nextCell === null) {
        delete grades[assignmentId]
      } else {
        grades[assignmentId] = nextCell
      }
      return { ...student, grades }
    }),
  }
}

/** The cell a save produces, carrying over the id of the grade it replaces. */
function optimisticCell(
  previous: GradebookCellGrade | undefined,
  pointsEarned: number | undefined,
  status: GradebookCellGrade['status'],
  feedback: string | undefined,
  totalPoints: number | undefined
): GradebookCellGrade {
  // A percentage needs the assignment's total, which lives in a sibling cache.
  // Without it, show the points and let the refetch supply the percentage rather
  // than divide by a guess.
  const percentage =
    pointsEarned != null && totalPoints
      ? Math.round((pointsEarned / totalPoints) * 100)
      : (previous?.percentage ?? null)

  return {
    // A brand-new grade has no id until the server answers, so the cell carries
    // "" for the length of the round trip. `adoptSavedGradeIds` replaces it with
    // the real id in `onSuccess`, before the refetch and before the mutation
    // resolves, so the placeholder never outlives the save — but read it through
    // a falsy check anyway (a grade id is never ""), because a cell addressed by
    // "" would delete against the collection endpoint rather than a grade.
    gradeId: previous?.gradeId ?? '',
    percentage,
    pointsEarned,
    scaleKey: previous?.scaleKey,
    scoreCode: previous?.scoreCode,
    status,
    feedback: feedback ?? previous?.feedback,
  }
}

/**
 * Give the optimistically-added cells the ids the server just minted.
 *
 * Without this, a newly created grade sits in the cache under `gradeId: ''`
 * until the invalidated refetch lands. That window is normally invisible, but if
 * the refetch is cancelled — a second save on the same roster does exactly that,
 * since `invalidateQueries` defaults to `cancelRefetch` — the placeholder
 * survives, and the next thing to address that cell by id would target
 * `/api/grades/` rather than a grade.
 */
function adoptSavedGradeIds(
  queryClient: QueryClient,
  termCourseId: string,
  saved: Array<Pick<Grade, 'id' | 'studentId' | 'assignmentId'>>
) {
  queryClient.setQueriesData<GradebookRoster>(
    { queryKey: gradebookKeys.students(termCourseId) },
    (roster) => {
      if (!roster) return roster
      return saved.reduce((next, grade) => {
        const cell = next.students.find((s) => s.id === grade.studentId)?.grades[
          grade.assignmentId
        ]
        if (!cell || cell.gradeId === grade.id) return next
        return patchRosterCell(next, grade.studentId, grade.assignmentId, {
          ...cell,
          gradeId: grade.id,
        })
      }, roster)
    }
  )
}

export function useSaveGrade(termCourseId: string | null) {
  const queryClient = useQueryClient()

  return useOptimisticMutation({
    mutationFn: saveGrade,
    onSuccess: (grade) => {
      if (termCourseId && grade) adoptSavedGradeIds(queryClient, termCourseId, [grade])
    },
    optimistic: (variables) => {
      if (!termCourseId) return []
      const assignments = queryClient.getQueryData<CachedAssignments>(
        gradebookKeys.assignments(termCourseId)
      )
      const totalPoints = assignments?.find((a) => a.id === variables.assignmentId)?.totalPoints

      return [
        cachePatch<GradebookRoster>(gradebookKeys.students(termCourseId), (roster) => {
          const previous = roster.students.find((s) => s.id === variables.studentId)?.grades[
            variables.assignmentId
          ]
          return patchRosterCell(
            roster,
            variables.studentId,
            variables.assignmentId,
            optimisticCell(
              previous,
              variables.pointsEarned,
              variables.status,
              variables.feedback,
              totalPoints
            )
          )
        }),
      ]
    },
    // A grade write moves the server's weighted term grade, which lives on the
    // enrollment — so the enrollments read is part of what a mark makes stale,
    // not just the marks themselves. Without it the headline column keeps the
    // number from before the save for the length of the app's staleTime.
    //
    // The child's own record and their parent's portal report that same grade,
    // which is the rest of what `cacheSync.grades` covers.
    invalidateKeys: (variables) =>
      cacheSync.grades({
        termCourseId: termCourseId ?? undefined,
        studentId: variables.studentId,
      }),
  })
}

export function useDeleteGrade(termCourseId: string | null) {
  return useOptimisticMutation({
    mutationFn: deleteGrade,
    optimistic: (gradeId) =>
      termCourseId
        ? [
            // The cell is addressed by grade id here, not by student/assignment,
            // so the roster is searched for whichever cell holds it.
            cachePatch<GradebookRoster>(
              gradebookKeys.students(termCourseId),
              (roster) => ({
                ...roster,
                students: roster.students.map((student) => {
                  const assignmentId = Object.keys(student.grades).find(
                    (key) => student.grades[key].gradeId === gradeId
                  )
                  if (!assignmentId) return student
                  const grades = { ...student.grades }
                  delete grades[assignmentId]
                  return { ...student, grades }
                }),
              })
            ),
          ]
        : [],
    // See `useSaveGrade`. This one is addressed by grade id, so which student it
    // belongs to is not knowable here and every student record is invalidated
    // rather than one.
    invalidateKeys: cacheSync.grades({ termCourseId: termCourseId ?? undefined }),
  })
}

export function useCreateAssignment(termCourseId: string | null) {
  return useOptimisticMutation({
    mutationFn: createAssignment,
    // The new assignment's id and server-side defaults are not knowable here, so
    // this one waits for the refetch rather than inventing a row. The awaited
    // invalidation still means the caller never sees the list without it.
    invalidateKeys: termCourseId ? cacheSync.assignments({ termCourseId }) : [],
  })
}

export function useUpdateAssignment(termCourseId: string | null) {
  return useOptimisticMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateAssignment>[1] }) =>
      updateAssignment(id, data),
    optimistic: (v) =>
      termCourseId
        ? [
            cachePatch<CachedAssignments>(gradebookKeys.assignments(termCourseId), (assignments) =>
              assignments.map((assignment) =>
                assignment.id === v.id
                  ? {
                      ...assignment,
                      // The cached row is the grid's projection of an assignment,
                      // not the API shape: only these three fields exist to
                      // repaint.
                      name: v.data.assignmentName ?? assignment.name,
                      totalPoints: v.data.totalPoints ?? assignment.totalPoints,
                      // An explicit null is "clear it", which `??` would read as
                      // "say nothing" and repaint with the deadline just removed.
                      dueDate:
                        v.data.dueDate === undefined
                          ? assignment.dueDate
                          : (v.data.dueDate ?? undefined),
                    }
                  : assignment
              )
            ),
          ]
        : [],
    // Re-pointing or removing an assignment re-weights the term grade the
    // enrollment carries, the same as a mark does — and moves what the calendar
    // says is due.
    invalidateKeys: termCourseId ? cacheSync.assignments({ termCourseId }) : [],
  })
}

export function useDeleteAssignment(termCourseId: string | null) {
  return useOptimisticMutation({
    mutationFn: deleteAssignment,
    optimistic: (assignmentId) =>
      termCourseId
        ? [
            cachePatch<CachedAssignments>(
              gradebookKeys.assignments(termCourseId),
              (assignments) => assignments.filter((a) => a.id !== assignmentId)
            ),
            // Its column of scores goes with it.
            cachePatch<GradebookRoster>(gradebookKeys.students(termCourseId), (roster) => ({
              ...roster,
              students: roster.students.map((student) => {
                if (!(assignmentId in student.grades)) return student
                const grades = { ...student.grades }
                delete grades[assignmentId]
                return { ...student, grades }
              }),
            })),
          ]
        : [],
    // Re-pointing or removing an assignment re-weights the term grade the
    // enrollment carries, the same as a mark does — and moves what the calendar
    // says is due.
    invalidateKeys: termCourseId ? cacheSync.assignments({ termCourseId }) : [],
  })
}

export function useSubmitGrades(termCourseId: string | null) {
  return useOptimisticMutation({
    mutationFn: async ({
      submissionId,
      termId,
    }: {
      submissionId?: string
      termId: string
    }) => {
      if (submissionId) {
        await api.put(`/api/term-submissions/${submissionId}/submit`)
      } else {
        const createRes = await api.post<{ submission: TermSubmission }>(
          '/api/term-submissions',
          { termId, termCourseId }
        )
        await api.put(`/api/term-submissions/${createRes.data.submission.id}/submit`)
      }
    },
    optimistic: () =>
      termCourseId
        ? [
            // Flip the banner to "submitted" straight away. A first-time submit
            // has no cached row to patch, and `cachePatch` skips entries that
            // were never fetched, so that case falls through to the refetch.
            cachePatch<TermSubmission | null>(gradebookKeys.submission(termCourseId), (submission) =>
              submission ? { ...submission, status: 'submitted' as const } : submission
            ),
          ]
        : [],
    // Not just this course's banner: handing a term in is what puts a row on the
    // admin's approval queue and on the teacher's own list of submissions, and
    // it moves a cell of the Grade Posting coverage table.
    invalidateKeys: cacheSync.submissions({ termCourseId: termCourseId ?? undefined }),
  })
}

export function useSaveEnrollments(termCourseId: string | null) {
  return useOptimisticMutation({
    mutationFn: async ({
      currentEnrollments,
      selectedStudentIds,
    }: {
      currentEnrollments: Enrollment[]
      selectedStudentIds: Set<string>
    }) => {
      // Only consider active enrollments as "current" - withdrawn/dropped students can be re-enrolled
      const activeEnrollments = currentEnrollments.filter((e) => e.status === 'active')
      const currentStudentIds = new Set(activeEnrollments.map((e) => e.studentId))

      // Students to enroll
      const toEnroll = Array.from(selectedStudentIds).filter(
        (id) => !currentStudentIds.has(id)
      )

      // Students to unenroll (withdraw)
      const toUnenroll = activeEnrollments.filter(
        (e) => !selectedStudentIds.has(e.studentId)
      )

      await Promise.all([
        ...toEnroll.map((studentId) =>
          api.post('/api/enrollments', {
            studentId,
            termCourseId,
            status: 'active',
            enrollmentDate: new Date().toISOString().split('T')[0],
          })
        ),
        ...toUnenroll.map((e) => api.delete(`/api/enrollments/${e.id}`)),
      ])
    },
    optimistic: (v) =>
      termCourseId
        ? [
            // Removals only. A new enrollment has no id yet, and the roster query
            // key is derived from the active student ids (see
            // `gradebookRosterQuery`), so inventing one would point the grid at a
            // cache entry that does not exist. Dropping the withdrawn rows is
            // safe and is the half a teacher notices.
            cachePatch<Enrollment[]>(gradebookKeys.enrollments(termCourseId), (enrollments) =>
              enrollments.filter(
                (e) => e.status !== 'active' || v.selectedStudentIds.has(e.studentId)
              )
            ),
          ]
        : [],
    invalidateKeys: cacheSync.enrollments({ termCourseId: termCourseId ?? undefined }),
  })
}
